







































import useAppConfig from "@/composables/useAppConfig";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import useChatInput from "@/composables/v3/useChatInput";
import useTemplates from "@/composables/v3/useTemplates";
import useTyping from "@/composables/v3/useTyping";
import { TemplateInterface } from "@/interfaces/template.interface";
import { useWebsockets } from "@/libs/websockets";
import { useChannelsStore } from "@/store/useChannelsStore";
import { useChatsStore } from "@/store/useChatsStore";
import { useTemplatesStore } from "@/store/useTemplatesStore";
import { InboxModuleType } from "@/types/inboxModule.type";
import { isChatAssignedToMeV21, shouldAssignV21 } from "@/utils/chats";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { computed, defineComponent, onMounted, Ref, watch } from "@vue/composition-api";
import { useFocus } from "@vueuse/core";
import { BFormTextarea, BInputGroup } from "bootstrap-vue";
import { storeToRefs } from "pinia";

export default defineComponent({
    methods: { computed },
    components: { BFormTextarea, BInputGroup },
    emits: ["send-message", "upload-files"],
    setup(props, ctx) {
        const chatsStore = useChatsStore();
        const channelsStore = useChannelsStore();
        const { message, messageRef, maxMessageChars } = useChatInput();
        const { activeTabIndex, activeChatTemplatesTab, selectedTemplate, isTemplate, hasMediaTemplateFilesUploaded } =
            useTemplates();
        const { setStoredMessages, getStoredMessage } = useStoredMessages();
        const { isTemplateValid } = useTemplates();
        const { inboxModuleType } = useInboxModuleType();

        //----------------------------------------------------------------------

        onMounted(() => {
            useFocus(messageRef, { initialValue: true });
        });

        const { enterToSend } = useAppConfig();

        watch(
            () => chatsStore.activeChat?.chat.id,
            () => {
                // If a message has already been set from local storage for example, check if that is a template
                const { fetchTemplates } = useTemplatesStore();
                const storedMessage = getStoredMessage({
                    inboxModuleType: InboxModuleType.MESSAGING,
                    chatId: chatsStore.activeChat.chat.id,
                });

                if (storedMessage?.message) {
                    fetchTemplates(
                        RequestQueryBuilder.create({
                            join: { field: "channels", select: ["id"] },
                            fields: ["title", "content", "components", "mediaType", "assetId", "assetIsLocked"],
                            filter: [
                                { field: "channels.id", operator: "$eq", value: chatsStore.activeChat?.chat.channelId },
                            ],
                        })
                    ).then(() => {
                        const { templates }: { templates: Ref<TemplateInterface[]> } = storeToRefs(useTemplatesStore());

                        const matchedTemplate = templates.value.find((template) => {
                            const [isValid] = isTemplateValid(template.content, storedMessage.message);
                            return isValid;
                        });

                        if (matchedTemplate) {
                            // @ts-ignore
                            selectedTemplate.value = matchedTemplate;
                            const [isValid] = isTemplateValid(
                                selectedTemplate.value.content,
                                storedMessage.message,
                                channelsStore.activeChatChannel
                            );
                            isTemplate.value = isValid;
                        }
                    });
                }
            },
            {
                immediate: true,
            }
        );

        const sendWithEnter = (): boolean => {
            if (parseInt(enterToSend.value)) {
                if (chatsStore.isActiveChatEnabled) {
                    ctx.emit("send-message");
                } else if (isTemplate.value && !selectedTemplate.value.mediaType) {
                    ctx.emit("send-message");
                } else if (
                    isTemplate.value &&
                    selectedTemplate.value.mediaType &&
                    hasMediaTemplateFilesUploaded.value
                ) {
                    ctx.emit("send-message");
                }
            }

            return false;
        };

        const sendWithCtrlEnter = (): boolean => {
            if (!parseInt(enterToSend.value)) {
                if (chatsStore.isActiveChatEnabled) {
                    ctx.emit("send-message");
                } else if (isTemplate.value && !selectedTemplate.value.mediaType) {
                    ctx.emit("send-message");
                } else if (
                    isTemplate.value &&
                    selectedTemplate.value.mediaType &&
                    hasMediaTemplateFilesUploaded.value
                ) {
                    ctx.emit("send-message");
                }
            }

            return false;
        };

        //----------------------------------------------------------------------

        const paste = (event) => {
            if (!channelsStore.activeChatChannel) {
                return;
            }

            if (!event.clipboardData || event.clipboardData.getData("text/plain")) {
                return;
            }

            const files: FileList = event.clipboardData?.files || event.clipboardData.dataTransfer?.files;
            if (!files || files.length === 0) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            ctx.emit("upload-files", files);
        };

        //----------------------------------------------------------------------

        const websockets = useWebsockets();
        const { onStartTyping, onStopTyping } = useTyping(websockets);
        return {
            paste,
            onStopTyping,
            setStoredMessages,
            sendWithEnter,
            sendWithCtrlEnter,
            chatsStore,
            message,
            messageRef,
            onStartTyping,
            activeChatTemplatesTab,
            activeTabIndex,
            inboxModuleType,
            maxMessageChars,
            isChatAssignedToMeV21,
            shouldAssignV21,
            channelsStore,
        };
    },
});
